/**
 * Employee Route Component
 *
 * Handles /emp: login → (forced password change) → menu → document flow.
 * localStorage is a UI hint; GET /api/auth/employee/me restores the server session.
 */
import React, { useEffect, useState } from 'react';
import EmployeeLogin from './login/login';
import EmployeeChangePassword from './login/ChangePasswordForm';
import EmployeeMenu from './homepage/menu';
import SubmitDocumentsFlow from './submitDocuments/SubmitDocumentsFlow';
import EmployeeMessages from './messages/EmployeeMessages';
import {
  clearEmployeeSession,
  getEmployeeEmail,
  isEmployeeAuthenticated,
  mustChangeEmployeePassword,
  setEmployeeSession,
} from './auth/employeeAuthStorage';
import { fetchEmployeeMe, logoutEmployee } from '../../services/employeeAuthApi';
import { installEmployeeFetchInterceptor } from '../../utils/employeeFetch';

type EmployeeView = 'menu' | 'submit-documents' | 'edit-documents' | 'messages' | 'change-password';

const EmployeeRoute: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [view, setView] = useState<EmployeeView>('menu');

  const applyLocal = () => {
    const ok = isEmployeeAuthenticated() && getEmployeeEmail() != null;
    setIsAuthenticated(ok);
    setMustChangePassword(mustChangeEmployeePassword());
  };

  useEffect(() => {
    let cancelled = false;

    const restore = async () => {
      const localOk = isEmployeeAuthenticated() && getEmployeeEmail() != null;
      try {
        const me = await fetchEmployeeMe();
        if (cancelled) return;
        if (!localOk) {
          await logoutEmployee();
          clearEmployeeSession();
          setIsAuthenticated(false);
          setMustChangePassword(false);
          return;
        }
        setEmployeeSession(me.email, me.mustChangePassword);
        setIsAuthenticated(true);
        setMustChangePassword(me.mustChangePassword);
      } catch {
        if (cancelled) return;
        clearEmployeeSession();
        setIsAuthenticated(false);
        setMustChangePassword(false);
      } finally {
        if (!cancelled) setAuthChecked(true);
      }
    };

    void restore();

    const handleAuthChange = () => applyLocal();
    const handleExpired = () => {
      void logoutEmployee();
      clearEmployeeSession();
      setIsAuthenticated(false);
      setMustChangePassword(false);
      setView('menu');
    };

    window.addEventListener('employee-auth-change', handleAuthChange);
    window.addEventListener('employee-session-expired', handleExpired);

    return () => {
      cancelled = true;
      window.removeEventListener('employee-auth-change', handleAuthChange);
      window.removeEventListener('employee-session-expired', handleExpired);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    return installEmployeeFetchInterceptor();
  }, [isAuthenticated]);

  const handleLogout = () => {
    void logoutEmployee();
    clearEmployeeSession();
    setView('menu');
    window.dispatchEvent(new Event('employee-auth-change'));
  };

  if (!authChecked) {
    return <EmployeeLogin />;
  }

  if (!isAuthenticated) return <EmployeeLogin />;

  if (mustChangePassword) {
    return <EmployeeChangePassword forced onLogout={handleLogout} />;
  }

  if (view === 'change-password') {
    return (
      <EmployeeChangePassword
        onLogout={handleLogout}
        onBack={() => setView('menu')}
      />
    );
  }

  if (view === 'submit-documents') {
    return (
      <SubmitDocumentsFlow onBackToMenu={() => setView('menu')} />
    );
  }

  if (view === 'edit-documents') {
    return (
      <SubmitDocumentsFlow onBackToMenu={() => setView('menu')} mode="edit" />
    );
  }

  if (view === 'messages') {
    return <EmployeeMessages onBack={() => setView('menu')} />;
  }

  return (
    <EmployeeMenu
      onStartSubmitDocuments={() => setView('submit-documents')}
      onStartEditDocuments={() => setView('edit-documents')}
      onOpenMessages={() => setView('messages')}
      onChangePassword={() => setView('change-password')}
      onLogout={handleLogout}
    />
  );
};

export default EmployeeRoute;
